/**
 * Calendar module contracts. The calendar shows MY items only (the signed-in user's own people):
 * next contact dates, promises (ours / theirs), meetings, reminders (next-step dates), renewals, their deadlines,
 * strategy deadlines for my areas, and festival / season dates from each area's profile calendar.
 * All dates are IST calendar days (YYYY-MM-DD). The server decides which items can be moved and how
 * (`move`); moving goes through the existing endpoints and their guards, never a calendar-only write.
 */
import { z } from 'zod';
import { Id, IsoDate, VerticalKey } from './common.js';

export const CAL_PREFIX = '/cal';

export const CalItemType = z.enum([
  'next_contact', 'promise_ours', 'promise_theirs', 'meeting', 'reminder', 'renewal', 'deadline', 'strategy', 'festival',
]);
export type CalItemType = z.infer<typeof CalItemType>;

/** How an item is rescheduled: which existing endpoint the web calls. null = cannot be moved from the calendar. */
export const CalMove = z.enum(['watch', 'commitment', 'renewal']).nullable();
export type CalMove = z.infer<typeof CalMove>;

export const CalItem = z.object({
  /** Stable id: `<type>:<source id>` (a festival uses `festival:<area>:<date>:<name>`). */
  id: z.string().min(1).max(300),
  type: CalItemType,
  date: IsoDate,
  title: z.string(),
  detail: z.string().nullable(),
  verticalKey: VerticalKey,
  relationshipId: Id.nullable(),
  /** The record the move endpoint acts on (relationship id or commitment id). */
  sourceId: z.string().nullable(),
  /** Optimistic-concurrency version of that record (updated_at ms). */
  version: z.number().int().nonnegative().nullable(),
  move: CalMove,
  /** Before today (IST) and still open. Computed by the server. */
  overdue: z.boolean(),
});
export type CalItem = z.infer<typeof CalItem>;

/** Max 62 days per request (a month grid with its lead/trail days fits). */
export const CalRangeQuery = z.object({
  from: IsoDate,
  to: IsoDate,
  vertical: VerticalKey.optional(),
  /** Comma separated CalItemType list; omitted = all. */
  types: z.string().max(300).optional(),
}).strict();
export type CalRangeQuery = z.infer<typeof CalRangeQuery>;

export const CalRange = z.object({
  from: IsoDate,
  to: IsoDate,
  today: IsoDate,
  timezone: z.literal('Asia/Kolkata'),
  items: z.array(CalItem),
  /** Counts by type for the filter chips, computed by the server over the returned items. */
  counts: z.record(CalItemType, z.number().int().nonnegative()),
});
export type CalRange = z.infer<typeof CalRange>;

export const CAL_MAX_DAYS = 62;

export const calRoutes = {
  calendarRange: { method: 'GET', path: '/cal/items', query: CalRangeQuery, response: CalRange },
} as const;
export type CalRouteName = keyof typeof calRoutes;
